import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { CheckCircle2, Play, X } from "lucide-react";
import { motion } from "framer-motion";
import { Stagger, Item } from "@/components/motion";

export default function CareerPath() {
  const { id } = useParams();
  const [path, setPath] = useState(null);
  const [all, setAll] = useState([]);
  const [openVideo, setOpenVideo] = useState(false);

  useEffect(() => {
    api.get(`/career/paths/${id}`).then((r) => setPath(r.data));
    api.get("/career/paths").then((r) => setAll(r.data));
    setOpenVideo(false);
  }, [id]);

  if (!path) return <Container><div className="font-heading text-xl">Loading path...</div></Container>;

  return (
    <Container>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="path-switcher">
        {all.map((p) => (
          <Link key={p.id} to={`/career/path/${p.id}`} data-testid={`path-tab-${p.id}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${p.id === id ? "bg-sl-primary nb-shadow" : "bg-white"}`}>
            {p.title.split(" ")[0]}
          </Link>
        ))}
      </div>
      <PageTitle eyebrow="CAREER ROADMAP" title={path.title} sub={path.description} />

      {path.video && (
        <button onClick={() => setOpenVideo(true)} data-testid="career-video" className="w-full text-left mb-8">
          <NbCard color="accent" className="!p-5 flex items-center gap-4 nb-hover">
            <div className="w-16 h-16 bg-white nb-border rounded-lg grid place-items-center shrink-0"><Play className="w-7 h-7" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-sl text-[10px] uppercase">GUIDANCE VIDEO</div>
              <div className="font-heading font-black text-xl md:text-2xl tracking-tight truncate">{path.video.title}</div>
              <div className="text-sm text-neutral-700">by {path.video.teacher}</div>
            </div>
            <Chip color="tertiary">WATCH NOW</Chip>
          </NbCard>
        </button>
      )}

      <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="roadmap">
        {path.roadmap.map((s, i) => (
          <Item key={i}>
            <NbCard color={["primary", "secondary", "tertiary", "accent"][i % 4]} className="!p-6 h-full">
              <Chip color="accent">STAGE {i + 1}</Chip>
              <h3 className="font-heading font-black text-2xl tracking-tight mt-2">{s.stage}</h3>
              <ul className="mt-4 space-y-2">
                {s.items.map((it, j) => (
                  <li key={j} className="flex gap-2 items-start"><CheckCircle2 className="w-4 h-4 mt-1 shrink-0" /><span>{it}</span></li>
                ))}
              </ul>
            </NbCard>
          </Item>
        ))}
      </Stagger>

      <div className="mt-8"><Link to="/career/mentors"><NbButton color="black">Meet mentors for this path →</NbButton></Link></div>

      {openVideo && path.video && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(false)} data-testid="career-video-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center">
              <div className="font-heading font-bold truncate">{path.video.title}</div>
              <button onClick={() => setOpenVideo(false)} data-testid="close-career-video" className="p-2 nb-border rounded-lg bg-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <iframe title={path.video.title} className="w-full h-full" src={`https://www.youtube.com/embed/${path.video.id}?rel=0&autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </motion.div>
        </div>
      )}
    </Container>
  );
}
